import { Check, Copy, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PinBoxes, PIN_LENGTH, suggestPin } from '../../../../components/inputs/PinBoxes';
import { ApiError } from '../../../../lib/apiError';
import { usersApi } from '../usersApi';
import type { UserDetail } from '../userTypes';

/**
 * The Sign-in PIN card's "Set PIN" — the PIN's twin of <ResetUserPasswordModal>.
 *
 * Opens with a PIN already suggested in both rows; the reader keeps it or types their own. The
 * holder must replace it at the PIN step of their next sign-in either way: an administrator
 * has seen this value, so it is a handover credential rather than the holder's own. Every
 * session is revoked server-side the moment it saves.
 *
 * **Suggested in the BROWSER** (`suggestPin`), and the server never returns it — the value
 * crosses the network once, on its way to the hash.
 *
 * **No footer Autogenerate**, for the reason the password modal gives: both rows are filled at
 * mount, so the contract the button exists to meet is met in zero clicks, and a footer button
 * beside "Suggest another" would be two names for one act.
 */
export function ResetUserPinModal({
  user,
  onClose,
  onDone,
}: {
  user: UserDetail | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [issuedPin, setIssuedPin] = useState<string | null>(null);

  const userId = user?.id;

  // Each opening offers a fresh PIN: the next account must not be shown the last one's.
  useEffect(() => {
    if (!userId) return;

    const suggested = suggestPin();
    setPin(suggested);
    setConfirmPin(suggested);
    setConfirmed(false);
    setError(null);
    setIssuedPin(null);
  }, [userId]);

  if (!user) return null;

  const complete = pin.length === PIN_LENGTH && confirmPin.length === PIN_LENGTH;
  const mismatch = complete && pin !== confirmPin;
  const canSave = complete && !mismatch && confirmed && !saving;

  async function save() {
    if (!user || !canSave) return;

    setSaving(true);
    setError(null);

    try {
      await usersApi.resetPin(user.id, { pin, mustChangePin: true });
      setIssuedPin(pin);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not set the PIN.', 0));
    } finally {
      setSaving(false);
    }
  }

  // The PIN takes over the dialog rather than closing it: nothing stores it, so it has to be
  // copied before this closes.
  if (issuedPin) {
    return (
      <Modal
        open
        onClose={() => {
          setIssuedPin(null);
          onDone();
        }}
        title="PIN set"
        size="lg"
        id="user-pin-issued-modal"
        footer={
          <Button
            id="user-pin-issued-done"
            variant="primary"
            onClick={() => {
              setIssuedPin(null);
              onDone();
            }}
          >
            Done
          </Button>
        }
      >
        <IssuedPin email={user.email} pin={issuedPin} />
      </Modal>
    );
  }

  const errorMessage = mismatch
    ? 'The two PINs do not match.'
    : (error?.fieldError('pin') ?? null);

  return (
    <Modal
      open
      onClose={onClose}
      title="Set sign-in PIN"
      size="lg"
      id="reset-user-pin-modal"
      confirm={{
        id: 'reset-user-pin-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="reset-user-pin-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="reset-user-pin-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && !error.fieldError('pin') ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
          {user.displayName} is signed out of every session and must replace this PIN the next
          time they sign in.
          {user.pinRequired
            ? null
            : ' A PIN is not required on this account yet, so it is not asked for until the requirement is switched on.'}
        </p>

        <div className="flex flex-col gap-2">
          <p className="text-center text-sm text-slate-700" aria-hidden="true">
            New sign-in PIN
          </p>
          <PinBoxes
            id="reset-user-pin"
            label="New sign-in PIN"
            describedBy="reset-user-pin-help"
            value={pin}
            onChange={(next) => {
              setPin(next);
              setError(null);
            }}
            disabled={saving}
            invalid={errorMessage !== null}
            // Not the operator's own credential: a password manager has nothing to offer here.
            autoComplete="off"
          />
          <span id="reset-user-pin-help" className="sr-only">
            Six characters, letters and numbers.
          </span>

          <Button
            id="reset-user-pin-suggest"
            type="button"
            variant="tertiary"
            leading={<Sparkles className="h-4 w-4" aria-hidden="true" />}
            disabled={saving}
            onClick={() => {
              // Fills BOTH rows, as at sign-in: a suggested PIN was never typed, so there is
              // no typo for the second row to catch.
              const suggested = suggestPin();
              setPin(suggested);
              setConfirmPin(suggested);
              setError(null);
            }}
            className="mx-auto"
          >
            Suggest another
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-center text-sm text-slate-700" aria-hidden="true">
            Confirm the PIN
          </p>
          <PinBoxes
            id="reset-user-pin-confirm-row"
            label="Confirm the PIN"
            describedBy={errorMessage ? 'reset-user-pin-error' : undefined}
            value={confirmPin}
            onChange={(next) => {
              setConfirmPin(next);
              setError(null);
            }}
            disabled={saving}
            invalid={errorMessage !== null}
            autoComplete="off"
          />
          {errorMessage ? (
            <p id="reset-user-pin-error" role="alert" className="text-center text-xs text-red-600">
              {errorMessage}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/** Shows the PIN exactly once, with an explicit warning that it will not reappear. */
function IssuedPin({ email, pin }: { email: string; pin: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is visible on screen regardless.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">
        The sign-in PIN for <span className="font-mono tabular-nums">{email}</span> has been set.
      </p>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
        <p className="text-xs font-medium text-amber-800">
          This PIN is shown once and is not stored anywhere. Copy it now and deliver it through a
          channel you trust.
        </p>

        <div className="mt-2.5 flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border border-amber-200 bg-white px-3 py-2 font-mono tabular-nums text-sm text-slate-800">
            {pin}
          </code>
          <Button
            id="user-pin-issued-copy"
            variant="secondary"
            onClick={() => void copy()}
            leading={
              copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />
            }
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
    </div>
  );
}
